import { useFormHandler } from "shared/hooks";
import { useCallback } from "react";
import { z } from "zod";
import {
  createMultilingual,
  getMultilingualString,
  phoneNumberRegex,
} from "shared/utils";
import {
  EnumAddressLocationType,
  EnumEntityReferenceType,
  EnumOperatorEnrollmentsOnboardingStatus,
  EnumOperatorType,
  EnumUserAccessOperatorsRole,
  EnumUserAccessOperatorsStatus,
  OperatorCreateMutationVariables,
  OperatorManyDocument,
  OperatorsByUserDocument,
  RoleType,
  useOperatorCreateMutation,
  UserCreateInviteMutationVariables,
  UserFindOneQuery,
  useUserCreateInviteMutation,
  useUserUpdateMutation,
} from "gql/generated.ts";
import { i18nNamespace, loadLanguagePacks } from "i18n.ts";
import i18n from "i18next";
import { getNameFromUser } from "shared/utils/getNameFromUser";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

const addOperatorFormSchema = z
  .object({
    businessName: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_NAME")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    email: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      )
      .email(
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_EMAIL_ADDRESS")}`,
      )
      .optional()
      .or(z.literal("")),
    website: z
      .string()
      .url(
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.PUBLIC_WEBSITE")}`,
      )
      .optional()
      .or(z.literal("")),
    phone: z
      .string()
      .regex(
        phoneNumberRegex,
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.LOCAL_PUBLIC_PHONE")}`,
      )
      .optional()
      .or(z.literal("")),
    tollFreePhone: z
      .string()
      .regex(
        phoneNumberRegex,
        `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.TOLL_FREE_PUBLIC_PHONE")}`,
      )
      .optional()
      .or(z.literal("")),
    businessType: z
      .string()
      .min(
        1,
        `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.BUSINESS_INFO.BUSINESS_TYPE.TITLE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      ),
    fullAddress: z.string().optional(),
    fullAddressObj: z
      .object({
        line1: z.string().optional().default(""),
        line2: z.string().optional().default(""),
        city: z.string().optional().default(""),
        subdivision: z.string().optional().default(""),
        postalCode: z.string().optional().default(""),
        country: z.string().optional().default(""),
        location: z
          .object({
            type: z.string().default("Point"),
            coordinates: z.object({
              lat: z.number().min(-90).max(90),
              lng: z.number().min(-180).max(180),
            }),
          })
          .optional(),
      })
      .partial()
      .optional(),
    invitedUser: z.object({
      email: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 2 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.EMAIL_ADDRESS")}`,
        )
        .email(
          `${i18n.t("core:FORM_ERRORS.INVALID")}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.EMAIL_ADDRESS")}`,
        )
        .optional()
        .or(z.literal("")),
      firstName: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.FIRST_NAME")}`,
        )
        .optional()
        .or(z.literal("")),
      lastName: z
        .string()
        .min(
          1,
          `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.LAST_NAME")}`,
        )
        .optional()
        .or(z.literal("")),
    }),
  })
  .superRefine((data, ctx) => {
    /*
     * We can't validate for a complete address anymore as not all operators
     * have a complete address.
     * 
    const fullAddressObj = data.fullAddressObj
      ? {
          line1: data.fullAddressObj.line1,
          line2: data.fullAddressObj.line2,
          city: data.fullAddressObj.city,
          subdivision: data.fullAddressObj.subdivision,
          postalCode: data.fullAddressObj.postalCode,
          country: data.fullAddressObj.country,
          lat: data.fullAddressObj.location?.coordinates.lat ?? 0,
          lng: data.fullAddressObj.location?.coordinates.lng ?? 0,
        }
      : {};
    const isAnyPropertyInAddressFilled = Object.values(fullAddressObj).some(
      (value) => value !== undefined && value !== "" && value !== 0,
    );

    // If any of the properties under the address are filled out, validate that
    // the full address is complete
    if (isAnyPropertyInAddressFilled) {
      if (!fullAddressObj.line1) {
        ctx.addIssue({
          message: `${i18n.t("core:SELECT_ADDRESS.BUSINESS_ADDRESS")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          path: ["fullAddressObj", "line1"],
          code: "custom",
        });
      }
      if (!fullAddressObj.city) {
        ctx.addIssue({
          message: `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.CITY")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          path: ["fullAddressObj", "city"],
          code: "custom",
        });
      }
      if (!fullAddressObj.subdivision) {
        ctx.addIssue({
          message: `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.SUBDIVISION")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          path: ["fullAddressObj", "subdivision"],
          code: "custom",
        });
      }
      if (!fullAddressObj.postalCode) {
        ctx.addIssue({
          message: `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.POSTAL_CODE")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          path: ["fullAddressObj", "postalCode"],
          code: "custom",
        });
      }
      if (!fullAddressObj.country) {
        ctx.addIssue({
          message: `${i18n.t("core:SELECT_ADDRESS.MANUAL_ADDRESS.COUNTRY")}${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
          path: ["fullAddressObj", "country"],
          code: "custom",
        });
      }
      if (fullAddressObj.lat === 0) {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("core:SELECT_ADDRESS.LATITUDE")}`,
          path: ["fullAddressObj", "location", "coordinates", "lat"],
          code: "custom",
        });
      }
      if (fullAddressObj.lng === 0) {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("core:SELECT_ADDRESS.LONGITUDE")}`,
          path: ["fullAddressObj", "location", "coordinates", "lng"],
          code: "custom",
        });
      }
    }
    */

    if (data.invitedUser.email) {
      if (data.invitedUser.firstName === "") {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.FIRST_NAME")}`,
          path: ["invitedUser.firstName"],
          code: "custom",
        });
      }
      if (data.invitedUser.lastName === "") {
        ctx.addIssue({
          message: `${i18n.t("core:FORM_ERRORS.PLEASE_ENTER", { count: 1 })}${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.INVITE_USER.LAST_NAME")}`,
          path: ["invitedUser.lastName"],
          code: "custom",
        });
      }
    }
  });

export type AddOperatorFormValues = z.infer<typeof addOperatorFormSchema>;

interface AddOperatorFormOptions {
  onSuccess?: (operatorName: string, invitedUser?: string) => void;
  onError?: (message: string) => void;
  onClose?: () => void;
  // This can been used to pass in connected org ids or a single org id
  organizationIds?: string[];
  user?: UserFindOneQuery["userFindOne"];
}

export const useAddOperatorForm = ({
  onSuccess,
  onError,
  onClose,
  organizationIds,
  user,
}: AddOperatorFormOptions) => {
  const defaultValues: AddOperatorFormValues = {
    businessName: "",
    email: "",
    website: "",
    phone: "",
    tollFreePhone: "",
    businessType: "",
    fullAddress: "",
    fullAddressObj: {
      line1: "",
      line2: "",
      city: "",
      subdivision: "",
      postalCode: "",
      country: "",
      location: {
        type: "Point",
        coordinates: {
          lat: 0,
          lng: 0,
        },
      },
    },
    invitedUser: {
      email: "",
      firstName: "",
      lastName: "",
    },
  };

  const formErrorMessage = (isInviteUserError?: boolean) =>
    isInviteUserError
      ? `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_USER_INVITE")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`
      : `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_OPERATOR")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`;

  const [createOperator] = useOperatorCreateMutation();
  const [createUserInvite] = useUserCreateInviteMutation();
  const [updateUser] = useUserUpdateMutation();

  /**
   * Function to get enrollments for `createOperator` payload
   * If user exists:
   * 1. Check if accesses are present on user and construct enrollments based on those
   * 2. If accesses are not present, use `onboardingOrganization` from `onboardingMetadata`
   *
   * If organizationIds exists, construct enrollment with that or those ids
   */
  const getEnrollments = useCallback(() => {
    const baseEnrollment = {
      onboardingStatus:
        EnumOperatorEnrollmentsOnboardingStatus.CompletedOnboarding,
      organizationControlled: false,
    };
    const enrollments = [];
    if (
      user &&
      (user.access.organizations.length > 0 ||
        user.onboardingMetadata?.onboardingOrganization)
    ) {
      if (user.access.organizations.length > 0) {
        const existingEnrollments = user.access.organizations.map(
          (organization) => {
            return { ...baseEnrollment, organization: organization?.entityId };
          },
        );
        enrollments.push(...existingEnrollments);
      } else if (user.onboardingMetadata?.onboardingOrganization) {
        enrollments.push({
          ...baseEnrollment,
          organization: user.onboardingMetadata.onboardingOrganization,
        });
      }
    } else if (organizationIds) {
      organizationIds.forEach((organizationId) => {
        enrollments.push({
          ...baseEnrollment,
          organization: organizationId,
        });
      });
    }
    return enrollments;
  }, [organizationIds, user]);

  return useFormHandler<AddOperatorFormValues>(
    useCallback(
      async (data) => {
        const enrollments = getEnrollments();

        /**
         * Condition to check that we have sufficient data for `enrollments`
         * in the `createOperator` payload
         *
         * Will call `onError` if `user` was provided and there is no organizations
         * present on either the `onboardingMetadata` or in their `access` array, OR
         * if no `organizationId` was passed into the hook
         */
        if (enrollments.length == 0) {
          onError?.(
            i18n.t(
              "directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.INVALID_ENROLLMENTS",
            ),
          );
          return;
        }

        if (data) {
          // If the fullAddressObj exists & line1 has a length greater than 0 -
          // based on the zod validation the rest of the address should be
          // filled out & will be accessible to use in the payload.
          const address =
            data.fullAddressObj &&
            data.fullAddressObj.line1 &&
            data.fullAddressObj.line1?.length > 0
              ? {
                  line1: data.fullAddressObj.line1 ?? "",
                  line2: data.fullAddressObj.line2 ?? "",
                  city: data.fullAddressObj.city ?? "",
                  subdivision: data.fullAddressObj.subdivision ?? "",
                  postalCode: data.fullAddressObj.postalCode ?? "",
                  country: data.fullAddressObj.country ?? "",
                  location: {
                    type:
                      (data.fullAddressObj.location
                        ?.type as EnumAddressLocationType) ?? "",
                    coordinates: [
                      data.fullAddressObj?.location?.coordinates?.lng ?? 0,
                      data.fullAddressObj?.location?.coordinates?.lat ?? 0,
                    ],
                  },
                }
              : undefined;
          const links = data.website ? { website: data.website } : undefined;

          const operatorPayload: OperatorCreateMutationVariables = {
            record: {
              claimed: true,
              enrollments,
              name: createMultilingual(data.businessName ?? ""),
              type: data.businessType
                ? (data.businessType as EnumOperatorType)
                : EnumOperatorType.Accommodation,
              contact: {
                email:
                  data.email && data.email.length > 0 ? data.email : undefined,
                phone:
                  data.phone && data.phone?.length > 0 ? data.phone : undefined,
                tollFreePublicPhone:
                  data.tollFreePhone && data.tollFreePhone.length > 0
                    ? data.tollFreePhone
                    : undefined,
                address,
                links,
              },
            },
          };
          const isInvitingUser = !!(
            data.invitedUser &&
            data.invitedUser.email &&
            data.invitedUser.firstName &&
            data.invitedUser.lastName
          );

          await createOperator({
            variables: operatorPayload,
            refetchQueries: [OperatorManyDocument],
          })
            .then(async (res) => {
              let operatorName: string = "";
              if (res.data && res.data?.operatorCreate?.record?.name) {
                operatorName = getMultilingualString(
                  res.data.operatorCreate.record.name,
                );
              }
              if (isInvitingUser) {
                onClose?.();
                const inviteUserPayload: UserCreateInviteMutationVariables = {
                  email: data.invitedUser?.email ?? "",
                  firstName: data.invitedUser?.firstName ?? "",
                  lastName: data.invitedUser?.lastName ?? "",
                  // When adding a user while creating an operator, we'll assume
                  // that we want to send an invitation email
                  sendInvitationEmail: true,
                  entity: {
                    _entityId: res.data?.operatorCreate?.record?._entityId,
                    type: EnumEntityReferenceType.Operator,
                  },
                  role: RoleType.Owner,
                };
                await createUserInvite({
                  variables: inviteUserPayload,
                })
                  .then((res) => {
                    const fullName = getNameFromUser(
                      res.data?.userCreateInvite,
                    );
                    onSuccess?.(operatorName, fullName);
                  })
                  .catch(() => {
                    onError?.(formErrorMessage(true));
                  });
              } else if (user) {
                /* Add the new operator to the user's `access` array */
                await updateUser({
                  variables: {
                    id: user._id,
                    record: {
                      access: {
                        ...user.access,
                        operators: [
                          ...user.access.operators,
                          {
                            entityId:
                              res.data?.operatorCreate?.record?._entityId,
                            role: EnumUserAccessOperatorsRole.Owner,
                            status: EnumUserAccessOperatorsStatus.Accepted,
                          },
                        ],
                      },
                    },
                  },
                  refetchQueries: [OperatorsByUserDocument],
                })
                  .then(() => onSuccess?.(operatorName))
                  .catch(() => {
                    onError?.(
                      i18n.t(
                        "directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.UPDATE_USER_ACCESS",
                      ),
                    );
                  });
              } else {
                onSuccess?.(operatorName);
              }
            })
            .catch(() => {
              onError?.(formErrorMessage());
            });
        }
      },
      [
        createOperator,
        createUserInvite,
        getEnrollments,
        onClose,
        onError,
        onSuccess,
        updateUser,
        user,
      ],
    ),
    defaultValues,
    { schema: addOperatorFormSchema },
  );
};
